import { type FC, type ReactNode, useContext, useRef } from "react";

import { type AssistantScope } from "@app_schema";

import {
  AssistantColumn,
  AssistantColumnEmpty,
  AssistantColumnView,
} from "@styled/assistant_column";
import { ChatForm } from "@styled/chat_form";
import { Loader } from "@styled/loader";
import { NoticePanel } from "@styled/notice_panel";
import { Notification } from "@styled/notification";
import { Sentence } from "@styled/sentence";

import { DashboardConversationsDetails } from "../../conversations/conversations_details";
import { DESCRIPTOR, LOCKED, placeholderOf } from "./assistant_scope_copy";
import { DraftingChips } from "./drafting_chips";
import { type SectionInChat } from "./drafting_section_item";
import { DraftingSectionsList } from "./drafting_sections_list";
import { DraftingSetupReading } from "./drafting_setup_review";
import { type PageTarget, pageTagHref } from "./page_tag";
import { SuggestedQuestionsList } from "./suggested_questions_list";
import { useActivePageTag } from "./use_active_page_tag";
import { AskContext } from "./use_ask";
import { type AssistantChats } from "./use_assistant_chats";
import { DraftingSectionsContext } from "./use_drafting_sections";
import { OpenPageContext } from "./use_open_page";
import { usePageTagClicks } from "./use_page_tag_clicks";

// On Drafting: each section's status in the chat open (by section id), whether
// any is the assistant's to draft, whether his Word file is still being read,
// and whether his sections show in place of the chat; + Add section shows his
// report.
export type DraftingInPane = {
  statuses: ReadonlyMap<string, SectionInChat>;
  draftable: boolean;
  reading: boolean;
  sectionsOpen: boolean;
  onSectionsOpen(open: boolean): void;
  onShowReport(): void;
};

// The assistant's half of the split: the chat (or a new chat's questions) over the composer; on Drafting,
// his sections or the chat, with Draft all and Items to check over the composer. Page tags and citation
// chips open pages in the other half, and rest while the assistant answers.
export const AssistantPane: FC<{
  chats: AssistantChats;
  scope: AssistantScope;
  available: boolean;
  agentReady: boolean;
  activePage: PageTarget | null;
  onOpenPage(target: PageTarget): void;
  hidden?: boolean;
  // Dimmed and still while his template is edited beside it.
  dimmed?: boolean;
  overlay?: ReactNode;
  drafting?: DraftingInPane;
}> = ({
  chats,
  scope,
  available,
  agentReady,
  activePage,
  onOpenPage,
  hidden,
  dimmed,
  overlay,
  drafting,
}) => {
  const reading = drafting?.reading;
  // Easy setup's review is up: nothing to draft or check yet.
  const settingUp = useContext(DraftingSectionsContext)?.setup.showing ?? false;
  const bodyRef = useRef<HTMLDivElement>(null);
  const onClick = usePageTagClicks(onOpenPage);
  useActivePageTag(bodyRef, activePage && pageTagHref(activePage));

  const disabled = chats.busy || !agentReady;
  const sectionsOpen = drafting?.sectionsOpen ?? false;
  const send: typeof chats.send = (input) => {
    drafting?.onSectionsOpen(false);
    return chats.send(input);
  };
  const onAsk = (question: string) => void send({ message: question });
  const sections = drafting && (
    <AssistantColumnEmpty>
      <DraftingSectionsList
        disabled={disabled}
        onAsk={onAsk}
        statuses={drafting.statuses}
        onClose={() => drafting.onSectionsOpen(false)}
        onShowReport={drafting.onShowReport}
      />
    </AssistantColumnEmpty>
  );
  let body: ReactNode;
  if (!available) {
    body = (
      <AssistantColumnEmpty>
        {reading ? (
          <DraftingSetupReading />
        ) : (
          <NoticePanel>{LOCKED[scope]}</NoticePanel>
        )}
      </AssistantColumnEmpty>
    );
  } else if (!chats.loaded) {
    body = <Loader />;
  } else if (chats.current) {
    body = (
      <>
        {sectionsOpen && sections}
        <AssistantColumnView hidden={sectionsOpen}>
          <DashboardConversationsDetails id={chats.current.id} embedded />
        </AssistantColumnView>
      </>
    );
  } else {
    // Drafting asks from his sections, never the suggested questions.
    body = drafting ? (
      (sectionsOpen && sections) || (
        <AssistantColumnEmpty>{null}</AssistantColumnEmpty>
      )
    ) : (
      <AssistantColumnEmpty>
        <SuggestedQuestionsList
          descriptor={DESCRIPTOR[scope]}
          questions={chats.questions}
          disabled={disabled}
          onAsk={onAsk}
        />
      </AssistantColumnEmpty>
    );
  }

  return (
    <AssistantColumn
      hidden={hidden}
      dimmed={dimmed}
      overlay={overlay}
      busy={chats.running}
      bodyRef={bodyRef}
      onClick={onClick}
      footerDimmed={!available}
      chips={
        drafting &&
        available &&
        !settingUp && (
          <DraftingChips
            disabled={disabled}
            draftable={drafting.draftable}
            onAsk={onAsk}
          />
        )
      }
      footer={
        chats.canAsk ? (
          <ChatForm
            layout="card"
            inputKind="chat"
            chat={send}
            loading={chats.busy}
            running={chats.running}
            stopping={chats.stopping}
            onStop={chats.stop}
            disabled={!agentReady || !available}
            placeholder={placeholderOf(scope, available, reading)}
          />
        ) : (
          <NoticePanel>
            Started by {chats.current?.user.name}. Start a new chat to ask.
          </NoticePanel>
        )
      }
    >
      {chats.error && (
        <Notification color="rose">
          <Sentence>{chats.error}</Sentence>
        </Notification>
      )}
      <OpenPageContext value={onOpenPage}>
        <AskContext value={drafting ? { onAsk, disabled } : null}>
          {body}
        </AskContext>
      </OpenPageContext>
    </AssistantColumn>
  );
};
